import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Users, UserCheck, BookOpen, Calendar, CheckSquare, ArrowUp, ArrowDown, GripVertical, Settings2, RotateCcw, Eye, EyeOff, Edit, ChevronUp, ChevronDown } from 'lucide-react';
import { api, dashboardApi, peopleApi, trialRequestsApi, type TrialRequestItem, type StandortKpiItem } from '../../../lib/api';
import { isOffeneAnfrage } from '../../../lib/trialRequests';
import { tasksApi, Task } from '../../../lib/tasks';
import { fetchTrials, setTrialStatus, TrialLessonDTO } from '../../../lib/trials';
import { formatDateTime } from '../../../lib/dateFormat';
import { markNoShow } from '../../../lib/trialActions';
import EnrollmentWizardModal from '../../../components/enrollments/EnrollmentWizardModal';
import TrialOfferWizardModal from '../../../components/trials/TrialOfferWizardModal';
import { toast } from 'sonner';
import CancelTrialWizardModal from '../../../components/trials/CancelTrialWizardModal';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Skeleton } from '../../../components/ui/skeleton';
import { Switch } from '../../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Link, useNavigate } from 'react-router-dom';
import { getUserSettings, saveUserSettings } from '../../../lib/userSettings';
import { useStandort } from '../../../components/layouts/use-standort';
import { Layout, Layouts, Responsive } from 'react-grid-layout/legacy';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../../../components/ui/dropdown-menu';
import TrialLessonGridCard from '../../../components/trials/TrialLessonGridCard';
import TrialRequestGridCard from '../../../components/trialRequests/TrialRequestGridCard';
import TaskForm, { type TaskFormValues } from '../../../components/tasks/TaskForm';
import { PaymentAnomaliesContent } from '../../../components/dashboard/PaymentAnomaliesWidget';
import { PaymentForecastContent } from '../../../components/dashboard/PaymentForecastWidget';
import { deriveTrialLessonWorkflow } from '../../../hooks/useTrialLessonWorkflow';
import { usePermissions } from '../../../components/rbac/use-permission';
import { P } from '../../../lib/rbac/permissions.generated';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

// Types
interface RecentActivityItem { id: number; type: string; description: string; createdAt: string; actorName?: string | null; personName?: string | null; }
interface BirthdayPerson { id: string; name: string; type: string; birthDate?: string | null; nextBirthday?: string }
interface BackendUser { id: number; vorname: string; nachname: string; email?: string; rolle?: string }

// Breite des Grids selbst messen: WidthProvider aus react-grid-layout/legacy blieb bei
// seinem Startwert von 1280 px hängen, die rechte Widget-Spalte ragte aus der Seite.
function useElementWidth<T extends HTMLElement>() {
  const [width, setWidth] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);
  // Callback-Ref: Der Container erscheint erst nach dem Laden, nicht beim ersten Render.
  const ref = useCallback((node: T | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) return;
    setWidth(node.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width;
      if (next) setWidth(next);
    });
    observer.observe(node);
    observerRef.current = observer;
  }, []);
  useEffect(() => () => observerRef.current?.disconnect(), []);
  return [ref, width] as const;
}

type DashboardWidgetId = 'todos' | 'trials' | 'trialRequests' | 'birthdays' | 'activities' | 'paymentAnomalies' | 'paymentForecast';
type DashboardBreakpoint = 'lg' | 'md' | 'sm';
type DashboardLayouts = Record<DashboardBreakpoint, Layout[]>;

const DASHBOARD_WIDGET_IDS: DashboardWidgetId[] = ['todos', 'trials', 'trialRequests', 'birthdays', 'activities', 'paymentAnomalies', 'paymentForecast'];
const BREAKPOINT_KEYS: DashboardBreakpoint[] = ['lg', 'md', 'sm'];
const WIDGET_LABELS: Record<DashboardWidgetId, string> = {
  todos: 'Aufgaben',
  trials: 'Probestunden',
  trialRequests: 'Probestundenanfragen',
  birthdays: 'Geburtstage',
  activities: 'Aktivitäten',
  paymentAnomalies: 'Zahlungs-Anomalien',
  paymentForecast: 'Zahlungs-Forecast',
};
const BREAKPOINTS: Record<DashboardBreakpoint, number> = { lg: 1200, md: 996, sm: 0 };
const COLS: Record<DashboardBreakpoint, number> = { lg: 2, md: 2, sm: 1 };
const DEFAULT_WIDGET_VISIBILITY: Record<DashboardWidgetId, boolean> = {
  todos: true,
  trials: true,
  trialRequests: true,
  birthdays: true,
  activities: true,
  paymentAnomalies: true,
  paymentForecast: true,
};
const DEFAULT_WIDGET_COLLAPSED: Record<DashboardWidgetId, boolean> = {
  todos: false,
  trials: false,
  trialRequests: false,
  birthdays: false,
  activities: false,
  paymentAnomalies: false,
  paymentForecast: false,
};
const COLLAPSED_WIDGET_HEIGHT = 1;

const DEFAULT_WIDGET_LAYOUTS: DashboardLayouts = {
  lg: [
    { i: 'todos', x: 0, y: 0, w: 1, h: 10, minW: 1, minH: 7 },
    { i: 'trials', x: 1, y: 0, w: 1, h: 10, minW: 1, minH: 7 },
    { i: 'trialRequests', x: 0, y: 10, w: 1, h: 10, minW: 1, minH: 6 },
    { i: 'birthdays', x: 1, y: 10, w: 1, h: 8, minW: 1, minH: 5 },
    { i: 'activities', x: 1, y: 18, w: 1, h: 10, minW: 1, minH: 6 },
    { i: 'paymentAnomalies', x: 0, y: 20, w: 1, h: 6, minW: 1, minH: 4 },
    { i: 'paymentForecast', x: 1, y: 26, w: 1, h: 6, minW: 1, minH: 4 },
  ],
  md: [
    { i: 'todos', x: 0, y: 0, w: 1, h: 10, minW: 1, minH: 7 },
    { i: 'trials', x: 1, y: 0, w: 1, h: 10, minW: 1, minH: 7 },
    { i: 'trialRequests', x: 0, y: 10, w: 1, h: 10, minW: 1, minH: 6 },
    { i: 'birthdays', x: 1, y: 10, w: 1, h: 8, minW: 1, minH: 5 },
    { i: 'activities', x: 1, y: 18, w: 1, h: 10, minW: 1, minH: 6 },
    { i: 'paymentAnomalies', x: 0, y: 20, w: 1, h: 6, minW: 1, minH: 4 },
    { i: 'paymentForecast', x: 1, y: 26, w: 1, h: 6, minW: 1, minH: 4 },
  ],
  sm: [
    { i: 'todos', x: 0, y: 0, w: 1, h: 9, minW: 1, minH: 6 },
    { i: 'trials', x: 0, y: 9, w: 1, h: 10, minW: 1, minH: 7 },
    { i: 'trialRequests', x: 0, y: 19, w: 1, h: 10, minW: 1, minH: 6 },
    { i: 'birthdays', x: 0, y: 29, w: 1, h: 8, minW: 1, minH: 5 },
    { i: 'activities', x: 0, y: 37, w: 1, h: 9, minW: 1, minH: 6 },
    { i: 'paymentAnomalies', x: 0, y: 46, w: 1, h: 6, minW: 1, minH: 4 },
    { i: 'paymentForecast', x: 0, y: 52, w: 1, h: 6, minW: 1, minH: 4 },
  ],
};

const cloneDefaultLayouts = (): DashboardLayouts => ({
  lg: DEFAULT_WIDGET_LAYOUTS.lg.map(item => ({ ...item })),
  md: DEFAULT_WIDGET_LAYOUTS.md.map(item => ({ ...item })),
  sm: DEFAULT_WIDGET_LAYOUTS.sm.map(item => ({ ...item })),
});

const cloneLayouts = (layouts: DashboardLayouts): DashboardLayouts => ({
  lg: layouts.lg.map(item => ({ ...item })),
  md: layouts.md.map(item => ({ ...item })),
  sm: layouts.sm.map(item => ({ ...item })),
});

const layoutItemSignature = (item: Layout): string => (
  `${item.i}:${item.x}:${item.y}:${item.w}:${item.h}:${item.minW ?? ''}:${item.minH ?? ''}`
);

const layoutsSignature = (layouts: DashboardLayouts): string => (
  BREAKPOINT_KEYS
    .map((bp) => layouts[bp].map(layoutItemSignature).sort((a, b) => a.localeCompare(b)).join('|'))
    .join('||')
);

const areLayoutsEqual = (a: DashboardLayouts, b: DashboardLayouts): boolean => (
  layoutsSignature(a) === layoutsSignature(b)
);

const normalizeWidgetLayouts = (raw: unknown): DashboardLayouts => {
  const next = cloneDefaultLayouts();
  if (!raw || typeof raw !== 'object') return next;
  const source = raw as Record<string, unknown>;
  BREAKPOINT_KEYS.forEach((bp) => {
    const fromSettings = Array.isArray(source[bp]) ? source[bp] as Array<Record<string, unknown>> : [];
    const byId = new Map<string, Layout>();
    fromSettings.forEach((entry) => {
      const id = typeof entry?.i === 'string' ? entry.i : '';
      if (!DASHBOARD_WIDGET_IDS.includes(id as DashboardWidgetId)) return;
      const x = Number(entry?.x);
      const y = Number(entry?.y);
      const w = Number(entry?.w);
      const h = Number(entry?.h);
      if ([x, y, w, h].some(Number.isNaN)) return;
      byId.set(id, {
        i: id,
        x,
        y,
        w: Math.max(1, w),
        h: Math.max(1, h),
        minW: entry?.minW ? Math.max(1, Number(entry.minW)) : undefined,
        minH: entry?.minH ? Math.max(1, Number(entry.minH)) : undefined,
      });
    });
    next[bp] = DEFAULT_WIDGET_LAYOUTS[bp].map(item => byId.get(item.i) || { ...item });
  });
  return next;
};

const normalizeWidgetVisibility = (raw: unknown): Record<DashboardWidgetId, boolean> => {
  const defaults = { ...DEFAULT_WIDGET_VISIBILITY };
  if (!raw || typeof raw !== 'object') return defaults;
  const source = raw as Record<string, unknown>;
  DASHBOARD_WIDGET_IDS.forEach((widgetId) => {
    if (typeof source[widgetId] === 'boolean') {
      defaults[widgetId] = source[widgetId];
    }
  });
  return defaults;
};

const normalizeWidgetCollapsed = (raw: unknown): Record<DashboardWidgetId, boolean> => {
  const defaults = { ...DEFAULT_WIDGET_COLLAPSED };
  if (!raw || typeof raw !== 'object') return defaults;
  const source = raw as Record<string, unknown>;
  DASHBOARD_WIDGET_IDS.forEach((widgetId) => {
    if (typeof source[widgetId] === 'boolean') {
      defaults[widgetId] = source[widgetId];
    }
  });
  return defaults;
};

const OfficeDashboard = () => {
  const navigate = useNavigate();
  const { selectedStandortId } = useStandort();
  // T-0196: Jede Rolle sieht nur Widgets, deren Daten sie sehen darf — vorher
  // zeigten sie ohne Recht einen Fehler oder blieben leer.
  const { can } = usePermissions();
  const erlaubteWidgetIds = useMemo(() => {
    const recht: Record<DashboardWidgetId, boolean> = {
      todos: can(P.arbeit.aufgaben.aufgaben_ansehen),
      trials: can(P.anmeldung.eintrag_ansehen),
      trialRequests: can(P.anfragen.anfragen_ansehen),
      birthdays: can(P.personen.personen.person_ansehen),
      activities: can(P.personen.personen.personenhistorie_ansehen),
      paymentAnomalies: can(P.buchhaltung.finanzdaten_und_zahlungen.zahlungen_ansehen),
      paymentForecast: can(P.buchhaltung.finanzdaten_und_zahlungen.zahlungen_ansehen),
    };
    return DASHBOARD_WIDGET_IDS.filter((widgetId) => recht[widgetId]);
  }, [can]);
  const [isLoading, setIsLoading] = useState(true);
  const pollRef = useRef<number | null>(null);
  const layoutSaveRef = useRef<number | null>(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [kpiOrder, setKpiOrder] = useState<number[]>([]);
  const [widgetLayouts, setWidgetLayouts] = useState<DashboardLayouts>(cloneDefaultLayouts());
  const [gridContainerRef, gridWidth] = useElementWidth<HTMLDivElement>();
  const [widgetVisibility, setWidgetVisibility] = useState<Record<DashboardWidgetId, boolean>>({ ...DEFAULT_WIDGET_VISIBILITY });
  const [widgetCollapsed, setWidgetCollapsed] = useState<Record<DashboardWidgetId, boolean>>({ ...DEFAULT_WIDGET_COLLAPSED });
  const [compactMode, setCompactMode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const editSnapshotRef = useRef<{
    widgetLayouts: DashboardLayouts;
    widgetVisibility: Record<DashboardWidgetId, boolean>;
    widgetCollapsed: Record<DashboardWidgetId, boolean>;
    compactMode: boolean;
    kpiOrder: number[];
  } | null>(null);
  const widgetExpandedHeightRef = useRef<Record<DashboardBreakpoint, Record<DashboardWidgetId, { h: number; minH?: number }>>>({
    lg: {} as Record<DashboardWidgetId, { h: number; minH?: number }>,
    md: {} as Record<DashboardWidgetId, { h: number; minH?: number }>,
    sm: {} as Record<DashboardWidgetId, { h: number; minH?: number }>,
  });
  const [currentBreakpoint, setCurrentBreakpoint] = useState<DashboardBreakpoint>('lg');
  const [standortKpis, setStandortKpis] = useState<StandortKpiItem[]>([]);
  const [activities, setActivities] = useState<RecentActivityItem[]>([]);
  const [todos, setTodos] = useState<Task[]>([]);
  const [trials, setTrials] = useState<TrialLessonDTO[]>([]);
  const [birthdays, setBirthdays] = useState<BirthdayPerson[]>([]);
  const [trialRequests, setTrialRequests] = useState<TrialRequestItem[]>([]);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [trialForCancel, setTrialForCancel] = useState<TrialLessonDTO | null>(null);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [trialForEnroll, setTrialForEnroll] = useState<TrialLessonDTO | null>(null);
  const [offerOpen, setOfferOpen] = useState(false);
  const [trialForOffer, setTrialForOffer] = useState<TrialLessonDTO | null>(null);
  const [taskEditOpen, setTaskEditOpen] = useState(false);
  const [taskForEdit, setTaskForEdit] = useState<Task | null>(null);
  const [taskUsers, setTaskUsers] = useState<Array<{ id: number; name: string }>>([]);
  const safeParseTrialDateTime = useCallback((date?: string, time?: string): Date | null => {
    const d = (date || '').trim();
    if (!d) return null;
    const t = (time || '').trim();
    const hhmm = t.length >= 5 ? t.slice(0, 5) : '00:00';
    const parsed = new Date(`${d}T${hhmm}:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, []);

  const isDesktopLayout = currentBreakpoint !== 'sm';
  const isDesktopEditMode = isDesktopLayout && isEditMode;
  const trialsOnDashboard = useMemo(() => trials.slice(0, 3), [trials]);
  const trialRequestsOnDashboard = useMemo(() => trialRequests.slice(0, compactMode ? 3 : 4), [compactMode, trialRequests]);
  const birthdaysOnDashboard = useMemo(() => birthdays.slice(0, compactMode ? 5 : 8), [birthdays, compactMode]);
  const activitiesOnDashboard = useMemo(() => activities.slice(0, compactMode ? 5 : 8), [activities, compactMode]);
  const visibleWidgetIds = useMemo(
    () => erlaubteWidgetIds.filter((widgetId) => widgetVisibility[widgetId]),
    [erlaubteWidgetIds, widgetVisibility]
  );
  const hiddenWidgetIds = useMemo(
    () => erlaubteWidgetIds.filter((widgetId) => !widgetVisibility[widgetId]),
    [erlaubteWidgetIds, widgetVisibility]
  );

  useEffect(() => {
    let active = true;
    const loadSettings = async () => {
      try {
        const settings = await getUserSettings();
        if (!active) return;
        const orderFromSettings = Array.isArray(settings?.dashboard?.kpiOrder)
          ? settings.dashboard.kpiOrder.map(Number).filter(v => !Number.isNaN(v))
          : [];
        setKpiOrder(orderFromSettings);
        setWidgetLayouts(normalizeWidgetLayouts(settings?.dashboard?.widgetLayouts));
        setWidgetVisibility(normalizeWidgetVisibility(settings?.dashboard?.widgetVisibility));
        setWidgetCollapsed(normalizeWidgetCollapsed(settings?.dashboard?.widgetCollapsed));
        setCompactMode(Boolean(settings?.dashboard?.compactMode));
      } catch {
        // Best effort: Dashboard bleibt auf Defaults benutzbar.
      } finally {
        if (active) setSettingsLoaded(true);
      }
    };
    loadSettings();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    return () => {
      if (layoutSaveRef.current !== null) {
        clearTimeout(layoutSaveRef.current);
        layoutSaveRef.current = null;
      }
    };
  }, []);

  const saveDashboardSettings = useCallback((next: {
    kpiOrder?: number[];
    widgetLayouts?: DashboardLayouts;
    widgetVisibility?: Record<DashboardWidgetId, boolean>;
    widgetCollapsed?: Record<DashboardWidgetId, boolean>;
    compactMode?: boolean;
  }) => {
    if (!settingsLoaded) return;
    const nextOrder = next.kpiOrder ?? kpiOrder;
    const nextLayouts = next.widgetLayouts ?? widgetLayouts;
    const nextVisibility = next.widgetVisibility ?? widgetVisibility;
    const nextCollapsed = next.widgetCollapsed ?? widgetCollapsed;
    const nextCompactMode = next.compactMode ?? compactMode;
    if (layoutSaveRef.current !== null) clearTimeout(layoutSaveRef.current);
    layoutSaveRef.current = globalThis.setTimeout(() => {
      saveUserSettings({
        dashboard: {
          kpiOrder: nextOrder,
          widgetLayouts: nextLayouts,
          widgetVisibility: nextVisibility,
          widgetCollapsed: nextCollapsed,
          compactMode: nextCompactMode,
        },
      }).catch(() => {
        toast.error('Dashboard-Layout konnte nicht gespeichert werden');
      });
    }, 300);
  }, [compactMode, kpiOrder, settingsLoaded, widgetCollapsed, widgetLayouts, widgetVisibility]);

  const handleWidgetLayoutsChange = useCallback((_current: Layout[], all: Layouts) => {
    const nextLayouts = normalizeWidgetLayouts(all);
    if (areLayoutsEqual(widgetLayouts, nextLayouts)) return;
    BREAKPOINT_KEYS.forEach((bp) => {
      nextLayouts[bp].forEach((item) => {
        const widgetId = item.i as DashboardWidgetId;
        if (!widgetCollapsed[widgetId]) {
          widgetExpandedHeightRef.current[bp][widgetId] = { h: item.h, minH: item.minH };
        }
      });
    });
    setWidgetLayouts(nextLayouts);
    if (isEditMode) {
      setHasUnsavedChanges(true);
      return;
    }
    saveDashboardSettings({ widgetLayouts: nextLayouts });
  }, [isEditMode, saveDashboardSettings, widgetCollapsed, widgetLayouts]);

  const moveKpi = useCallback((index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= standortKpis.length) return;
    const next = [...standortKpis];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    setStandortKpis(next);
    const nextOrder = next.map(it => Number(it.standortId));
    setKpiOrder(nextOrder);
    if (isEditMode) {
      setHasUnsavedChanges(true);
      return;
    }
    saveDashboardSettings({ kpiOrder: nextOrder });
  }, [isEditMode, saveDashboardSettings, standortKpis]);

  const toggleWidgetVisibility = useCallback((widgetId: DashboardWidgetId, visible: boolean) => {
    const next = { ...widgetVisibility, [widgetId]: visible };
    setWidgetVisibility(next);
    if (isEditMode) {
      setHasUnsavedChanges(true);
      return;
    }
    saveDashboardSettings({ widgetVisibility: next });
  }, [isEditMode, saveDashboardSettings, widgetVisibility]);

  const toggleWidgetCollapsed = useCallback((widgetId: DashboardWidgetId) => {
    const nextCollapsedValue = !widgetCollapsed[widgetId];
    const nextCollapsed = { ...widgetCollapsed, [widgetId]: nextCollapsedValue };
    setWidgetCollapsed(nextCollapsed);
    setWidgetLayouts((prev) => {
      const nextLayouts = cloneLayouts(prev);
      BREAKPOINT_KEYS.forEach((bp) => {
        nextLayouts[bp] = nextLayouts[bp].map((item) => {
          if (item.i !== widgetId) return item;
          if (nextCollapsedValue) {
            widgetExpandedHeightRef.current[bp][widgetId] = { h: item.h, minH: item.minH };
            return { ...item, h: COLLAPSED_WIDGET_HEIGHT, minH: COLLAPSED_WIDGET_HEIGHT };
          }
          const fallback = DEFAULT_WIDGET_LAYOUTS[bp].find((layoutItem) => layoutItem.i === widgetId);
          const restored = widgetExpandedHeightRef.current[bp][widgetId];
          return {
            ...item,
            h: Math.max(3, restored?.h ?? fallback?.h ?? item.h),
            minH: Math.max(3, restored?.minH ?? fallback?.minH ?? item.minH ?? 3),
          };
        });
      });
      if (isEditMode) {
        setHasUnsavedChanges(true);
      } else {
        saveDashboardSettings({ widgetLayouts: nextLayouts, widgetCollapsed: nextCollapsed });
      }
      return nextLayouts;
    });
  }, [isEditMode, saveDashboardSettings, widgetCollapsed]);

  const toggleCompactMode = useCallback((value: boolean) => {
    setCompactMode(value);
    if (isEditMode) {
      setHasUnsavedChanges(true);
      return;
    }
    saveDashboardSettings({ compactMode: value });
  }, [isEditMode, saveDashboardSettings]);

  const showAllWidgets = useCallback(() => {
    const next = { ...DEFAULT_WIDGET_VISIBILITY };
    setWidgetVisibility(next);
    if (isEditMode) {
      setHasUnsavedChanges(true);
      return;
    }
    saveDashboardSettings({ widgetVisibility: next });
    toast.success('Alle Widgets sind sichtbar');
  }, [isEditMode, saveDashboardSettings]);

  const resetDashboardLayout = useCallback(() => {
    const nextLayouts = cloneDefaultLayouts();
    setWidgetLayouts(nextLayouts);
    if (isEditMode) {
      setHasUnsavedChanges(true);
      return;
    }
    saveDashboardSettings({ widgetLayouts: nextLayouts });
    toast.success('Dashboard-Layout wurde zurückgesetzt');
  }, [isEditMode, saveDashboardSettings]);

  const startEditMode = useCallback(() => {
    editSnapshotRef.current = {
      widgetLayouts: cloneLayouts(widgetLayouts),
      widgetVisibility: { ...widgetVisibility },
      widgetCollapsed: { ...widgetCollapsed },
      compactMode,
      kpiOrder: [...kpiOrder],
    };
    setHasUnsavedChanges(false);
    setIsEditMode(true);
  }, [compactMode, kpiOrder, widgetCollapsed, widgetLayouts, widgetVisibility]);

  const cancelEditMode = useCallback(() => {
    const snapshot = editSnapshotRef.current;
    if (snapshot) {
      setWidgetLayouts(cloneLayouts(snapshot.widgetLayouts));
      setWidgetVisibility({ ...snapshot.widgetVisibility });
      setWidgetCollapsed({ ...snapshot.widgetCollapsed });
      setCompactMode(snapshot.compactMode);
      setKpiOrder([...snapshot.kpiOrder]);
    }
    setHasUnsavedChanges(false);
    setIsEditMode(false);
    editSnapshotRef.current = null;
    toast.info('Änderungen verworfen');
  }, []);

  const saveEditMode = useCallback(async () => {
    if (layoutSaveRef.current !== null) {
      clearTimeout(layoutSaveRef.current);
      layoutSaveRef.current = null;
    }
    try {
      await saveUserSettings({
        dashboard: {
          kpiOrder,
          widgetLayouts,
          widgetVisibility,
          widgetCollapsed,
          compactMode,
        },
      });
      setHasUnsavedChanges(false);
      setIsEditMode(false);
      editSnapshotRef.current = null;
      toast.success('Dashboard-Änderungen gespeichert');
    } catch {
      toast.error('Dashboard-Einstellungen konnten nicht gespeichert werden');
    }
  }, [compactMode, kpiOrder, widgetCollapsed, widgetLayouts, widgetVisibility]);

  const restoreWidget = useCallback((widgetId: DashboardWidgetId) => {
    toggleWidgetVisibility(widgetId, true);
  }, [toggleWidgetVisibility]);

  const toInputDatetime = useCallback((iso: string) => iso.replace(' ', 'T').slice(0, 16), []);

  const loadTaskUsers = useCallback(async () => {
    try {
      const all = await api.get<BackendUser[]>('/api/users');
      setTaskUsers(
        all.map((u) => ({
          id: u.id,
          name: `${u.vorname || ''} ${u.nachname || ''}`.trim() || `User #${u.id}`,
        }))
      );
    } catch {
      const fromTodos = Array.from(new Set(todos.map(t => t.assignedUserId)))
        .filter((id) => Number.isFinite(id))
        .map((id) => ({ id, name: `User #${id}` }));
      setTaskUsers(fromTodos);
    }
  }, [todos]);

  const refreshDashboardTodos = useCallback(async () => {
    if (!selectedStandortId) return;
    const today = new Date().toISOString().slice(0, 10);
    try {
      const openTasks = await tasksApi.list({ status: 'open', dueAtLte: today, standortId: selectedStandortId });
      setTodos(openTasks);
    } catch {
      // Best effort refresh after edit.
    }
  }, [selectedStandortId]);

  const openTaskEditDialog = useCallback(async (task: Task) => {
    setTaskForEdit(task);
    setTaskEditOpen(true);
    await loadTaskUsers();
  }, [loadTaskUsers]);

  const handleTaskEditSubmit = useCallback(async (values: TaskFormValues) => {
    if (!taskForEdit) return;
    try {
      await tasksApi.update(taskForEdit.id, values);
      toast.success('Aufgabe aktualisiert');
      setTaskEditOpen(false);
      setTaskForEdit(null);
      await refreshDashboardTodos();
    } catch (error: any) {
      toast.error(error?.message || 'Aufgabe konnte nicht aktualisiert werden');
    }
  }, [refreshDashboardTodos, taskForEdit]);

  useEffect(() => {
    const fetchDashboardData = async (withSpinner: boolean) => {
      if (!selectedStandortId) return;
      if (withSpinner) setIsLoading(true);
      try {
        const today = new Date().toISOString().slice(0, 10);
        // Jedes Widget lädt für sich: Ein fehlschlagender Aufruf (z. B. Geburtstage) darf
        // die übrigen Widgets nicht leer lassen.
        const [kpisRes, actsRes, tasksRes, trialsRes, bdaysRes, reqsRes] = await Promise.allSettled([
          dashboardApi.getKpisByStandort(),
          dashboardApi.getRecentActivities(10, selectedStandortId),
          tasksApi.list({ status: 'open', dueAtLte: today, standortId: selectedStandortId }),
          fetchTrials(undefined, today, selectedStandortId),
          peopleApi.getBirthdays(
            today,
            new Date(Date.now() + 1000 * 60 * 60 * 24 * 30).toISOString().slice(0, 10),
            undefined,
            selectedStandortId
          ),
          trialRequestsApi.list({ standort_id: selectedStandortId }),
        ]);
        const settled = <T,>(res: PromiseSettledResult<T>, fallback: T): T => {
          if (res.status === 'fulfilled') return res.value;
          console.error('Error fetching dashboard data:', res.reason);
          return fallback;
        };
        const kpisByStandort = settled(kpisRes, {} as Awaited<ReturnType<typeof dashboardApi.getKpisByStandort>>);
        const acts = settled(actsRes, [] as Awaited<ReturnType<typeof dashboardApi.getRecentActivities>>);
        const openTasks = settled(tasksRes, [] as Awaited<ReturnType<typeof tasksApi.list>>);
        const trialItems = settled(trialsRes, [] as Awaited<ReturnType<typeof fetchTrials>>);
        const bdays = settled(bdaysRes, [] as Awaited<ReturnType<typeof peopleApi.getBirthdays>>);
        const reqs = settled(reqsRes, [] as TrialRequestItem[]);
        // Standort-KPIs aufbereiten und sortieren
        const items = Object.values(kpisByStandort || {});
        const sortedByName = [...items].sort((a, b) => String(a.standortName).localeCompare(String(b.standortName)));
        const ordered = kpiOrder.length > 0
          ? kpiOrder
              .map(id => items.find(it => Number(it.standortId) === Number(id)))
              .filter((item): item is StandortKpiItem => Boolean(item))
          : sortedByName;
        setStandortKpis(ordered);
        setActivities(acts);
        setTodos(openTasks);
        const list = trialItems.filter(t => t.date <= today);
        setTrials(list);
        setBirthdays(bdays);
        // Safety net: falls Backend-Filter umgangen wird, im Dashboard hart nach ausgewähltem Standort filtern.
        // Nur offene Anfragen — ausgeblendete, umgewandelte und ans Altsystem
        // weitergeleitete standen sonst auch im Widget (T-0062).
        setTrialRequests(
          reqs.filter((r) => Number(r.standort_id) === Number(selectedStandortId) && isOffeneAnfrage(r)),
        );
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        if (withSpinner) setIsLoading(false);
      }
    };
    const startPolling = () => {
      if (pollRef.current !== null) return;
      pollRef.current = globalThis.setInterval(() => { fetchDashboardData(false); }, 30000);
    };
    const stopPolling = () => {
      if (pollRef.current !== null) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchDashboardData(false);
        startPolling();
      } else {
        stopPolling();
      }
    };

    fetchDashboardData(true);
    startPolling();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [kpiOrder, selectedStandortId]);

  const renderWidgetCard = (widgetId: DashboardWidgetId) => {
    const baseTitleClass = 'text-base font-semibold';
    const collapsed = widgetCollapsed[widgetId];
    const todosCountSuffix = todos.length > 0 ? ` (${todos.length})` : '';
    const trialRequestsCountSuffix = trialRequests.length > 0 ? ` (${trialRequests.length})` : '';
    const renderWidgetHeader = (title: string, linkTo?: string) => (
      <CardHeader className={collapsed ? 'px-3 py-2' : 'border-b p-4'}>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className={collapsed ? 'text-sm font-semibold' : baseTitleClass}>{title}</CardTitle>
          <div className="flex items-center gap-2">
            {!collapsed && linkTo ? (
              <Link to={linkTo} className="text-sm text-muted-foreground hover:text-primary">
                Alle anzeigen
              </Link>
            ) : null}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => toggleWidgetCollapsed(widgetId)}
              aria-label={collapsed ? `${WIDGET_LABELS[widgetId]} ausklappen` : `${WIDGET_LABELS[widgetId]} einklappen`}
              title={collapsed ? 'Ausklappen' : 'Einklappen'}
            >
              {collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
            </Button>
            {isDesktopEditMode ? (
              <div className="widget-drag-handle cursor-grab rounded border bg-muted/30 p-1 text-muted-foreground hover:bg-muted">
                <GripVertical size={14} />
              </div>
            ) : null}
          </div>
        </div>
      </CardHeader>
    );

    if (widgetId === 'todos') {
      if (collapsed) {
        return <Card className="h-full overflow-hidden">{renderWidgetHeader(`Offene Aufgaben${todosCountSuffix}`, '/dashboard/todo')}</Card>;
      }
      return (
        <Card className="h-full overflow-hidden">
          {renderWidgetHeader(`Offene Aufgaben${todosCountSuffix}`, '/dashboard/todo')}
          <div className="flex h-[calc(100%-72px)] flex-col">
            <div className="flex-1 min-h-0 overflow-y-auto p-3">
              {todos.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">Keine offenen Aufgaben</div>
              ) : todos.map(todo => {
                const due = new Date(todo.dueAt);
                const isOverdue = due.getTime() < new Date().setHours(0, 0, 0, 0);
                return (
                  <div key={todo.id} className="mb-3 last:mb-0">
                    <div className={`rounded-xl border p-3 ${isOverdue ? 'border-destructive/30 bg-destructive/10' : 'bg-card'}`}>
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <p className="text-sm font-medium">{todo.title}</p>
                          <p className="mt-1 text-xs text-muted-foreground">Fällig: {formatDateTime(todo.dueAt)}</p>
                          {isOverdue ? <Badge variant="destructive" className="mt-2">Überfällig</Badge> : null}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" aria-label="Aufgabe als erledigt markieren">
                            <CheckSquare size={16} />
                          </Button>
                          <Button variant="outline" size="sm" className="h-8 px-2 text-xs" onClick={() => { void openTaskEditDialog(todo); }}>
                            <Edit className="mr-1 h-3.5 w-3.5" />
                            Bearbeiten
                          </Button>
                          <Link to="/dashboard/todo">
                            <Button variant="outline" size="sm" className="h-8 px-2 text-xs">Öffnen</Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <CardContent className="border-t p-4">
              <Link to="/dashboard/todo">
                <Button variant="outline" size="sm" className="w-full">Alle Aufgaben anzeigen</Button>
              </Link>
            </CardContent>
          </div>
        </Card>
      );
    }

    if (widgetId === 'trials') {
      if (collapsed) {
        return <Card className="h-full overflow-hidden">{renderWidgetHeader('Probestunden (bis heute)', '/dashboard/trial-scheduler')}</Card>;
      }
      return (
        <Card className="h-full overflow-hidden">
          {renderWidgetHeader('Probestunden (bis heute)', '/dashboard/trial-scheduler')}
          <div className="flex h-[calc(100%-72px)] flex-col">
            <div className="flex-1 min-h-0 overflow-y-auto p-4">
              <div className="space-y-3">
                {trialsOnDashboard.length === 0 ? (
                  <div className="rounded-lg border p-4 text-sm text-muted-foreground">Keine Probestunden vorhanden</div>
                ) : trialsOnDashboard.map((trial) => {
                  const workflow = deriveTrialLessonWorkflow(trial);
                  const processState = (trial as any)?.process?.state as string | undefined;
                  const orderStatus = String((trial as any)?.order?.status || '').toLowerCase();
                  const canConfirmTeaching = orderStatus === 'angenommen' && processState !== 'not_started';
                  const canSendOffer = workflow.state === 'angebot' && !!trial.teacherId;
                  const allowRescheduleCancel = workflow.state === 'probestunde' || workflow.state === 'entscheidung';
                  return (
                    <TrialLessonGridCard
                      key={trial.id}
                      trial={trial}
                      hideTestingPanel
                      workflow={workflow}
                      onMarkOccurred={workflow.state === 'probestunde'
                        ? async () => {
                            try {
                              const trialDate = safeParseTrialDateTime(trial.date, trial.endTime || trial.startTime);
                              if (trialDate && trialDate.getTime() > Date.now()) {
                                const proceed = globalThis.confirm(
                                  'Der Termin liegt noch in der Zukunft. Wirklich trotzdem als "hat stattgefunden" markieren?'
                                );
                                if (!proceed) return;
                              }
                              const updated = await setTrialStatus(trial.id, 'completed');
                              setTrials((prev) => prev.map((t) => String(t.id) === String(updated.id) ? updated : t));
                              toast.success('Probestunde wurde als durchgeführt markiert');
                            } catch (error: any) {
                              toast.error(error?.message || 'Status konnte nicht gesetzt werden');
                            }
                          }
                        : undefined}
                      onReschedule={allowRescheduleCancel ? () => navigate('/dashboard/trial-scheduler') : undefined}
                      onCancel={allowRescheduleCancel ? () => {
                        setTrialForCancel(trial);
                        setCancelOpen(true);
                      } : undefined}
                      onResendConfirmation={workflow.state === 'probestunde' ? () => navigate('/dashboard/trial-scheduler') : undefined}
                      onOrderConfirm={canConfirmTeaching ? () => navigate('/dashboard/trial-scheduler') : undefined}
                      onSendOffer={canSendOffer ? () => {
                        setTrialForOffer(trial);
                        setOfferOpen(true);
                      } : undefined}
                      onEnrollment={(workflow.state === 'entscheidung' || workflow.state === 'anmeldung' || workflow.state === 'angebot' || workflow.state === 'unterricht') ? () => {
                        setTrialForEnroll(trial);
                        setEnrollOpen(true);
                      } : undefined}
                      onNotStarted={(workflow.state === 'entscheidung' || workflow.state === 'anmeldung' || workflow.state === 'angebot') && processState !== 'not_started'
                        ? async () => {
                            try {
                              const updated = await markNoShow(trial.id);
                              setTrials(prev => prev.map(t => String(t.id) === String(updated.id) ? updated : t));
                            } catch (error: any) {
                              toast.error(error?.message || 'Fehler');
                            }
                          }
                        : undefined}
                      onOpenHistory={() => navigate('/dashboard/trial-scheduler')}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </Card>
      );
    }

    if (widgetId === 'trialRequests') {
      if (collapsed) {
        return <Card className="h-full overflow-hidden">{renderWidgetHeader(`Neue Anfragen${trialRequestsCountSuffix}`, '/dashboard/trial-requests')}</Card>;
      }
      return (
        <Card className="h-full overflow-hidden">
          {renderWidgetHeader(`Neue Anfragen${trialRequestsCountSuffix}`, '/dashboard/trial-requests')}
          <div className="flex h-[calc(100%-72px)] flex-col">
            <div className="flex-1 min-h-0 overflow-y-auto p-4">
              <div className="space-y-3">
                {trialRequestsOnDashboard.length === 0 ? (
                  <div className="rounded-lg border p-4 text-sm text-muted-foreground">Keine neuen Anfragen</div>
                ) : trialRequestsOnDashboard.map(req => (
                  <TrialRequestGridCard
                    key={req.id}
                    item={req}
                    dashboardCompact
                    onOpenPersonAction={() => navigate('/dashboard/trial-requests')}
                    onOpenHistory={() => navigate('/dashboard/trial-requests')}
                    onOpenMessage={() => navigate('/dashboard/trial-requests')}
                    onCall={() => {
                      if (!req.telefon) {
                        toast.error('Keine Telefonnummer vorhanden');
                        return;
                      }
                      globalThis.location.href = `tel:${req.telefon}`;
                    }}
                    onCallFailed={() => {
                      toast.info('Bitte im Bereich Probestundenanfragen dokumentieren');
                    }}
                    onCommunicate={() => {
                      navigate('/dashboard/trial-requests');
                    }}
                    onOpenMerge={() => navigate('/dashboard/trial-requests')}
                    showMergeAction={false}
                    onConvert={() => navigate('/dashboard/trial-requests')}
                    canConvert
                    onHide={() => navigate('/dashboard/trial-requests')}
                    onAddQuickNote={async () => {
                      toast.info('Notizen bitte in der Probestundenanfragen-Übersicht erfassen');
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </Card>
      );
    }

    if (widgetId === 'paymentAnomalies') {
      if (collapsed) {
        return <Card className="h-full overflow-hidden">{renderWidgetHeader('Zahlungs-Anomalien', '/dashboard/accounting/payments')}</Card>;
      }
      return (
        <Card className="h-full overflow-hidden">
          {renderWidgetHeader('Zahlungs-Anomalien', '/dashboard/accounting/payments')}
          <div className="h-[calc(100%-72px)] overflow-y-auto p-4">
            <PaymentAnomaliesContent standortId={selectedStandortId ?? undefined} />
          </div>
        </Card>
      );
    }

    if (widgetId === 'paymentForecast') {
      if (collapsed) {
        return <Card className="h-full overflow-hidden">{renderWidgetHeader('Zahlungs-Forecast', '/dashboard/accounting/payments')}</Card>;
      }
      return (
        <Card className="h-full overflow-hidden">
          {renderWidgetHeader('Zahlungs-Forecast', '/dashboard/accounting/payments')}
          <div className="h-[calc(100%-72px)] overflow-y-auto p-4">
            <PaymentForecastContent standortId={selectedStandortId ?? undefined} daysAhead={30} />
          </div>
        </Card>
      );
    }

    if (widgetId === 'birthdays') {
      if (collapsed) {
        return <Card className="h-full overflow-hidden">{renderWidgetHeader('Anstehende Geburtstage')}</Card>;
      }
      return (
        <Card className="h-full overflow-hidden">
          {renderWidgetHeader('Anstehende Geburtstage')}
          <div className="flex h-[calc(100%-72px)] flex-col">
            <div className="flex-1 min-h-0 divide-y overflow-y-auto">
              {birthdaysOnDashboard.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">Keine anstehenden Geburtstage</div>
              ) : birthdaysOnDashboard.map(b => (
                <div key={b.id} className="p-4">
                  <p className="text-sm font-medium">{b.name}</p>
                  <p className="text-xs text-muted-foreground">{b.type} · Geburtstag: {b.birthDate || '—'} · Nächster: {b.nextBirthday || '—'}</p>
                </div>
              ))}
            </div>
          </div>
        </Card>
      );
    }

    if (collapsed) {
      return <Card className="h-full overflow-hidden">{renderWidgetHeader('Neueste Aktivitäten')}</Card>;
    }
    return (
      <Card className="h-full overflow-hidden">
        {renderWidgetHeader('Neueste Aktivitäten')}
        <div className="flex h-[calc(100%-72px)] flex-col">
          <div className="flex-1 min-h-0 divide-y overflow-y-auto">
            {activitiesOnDashboard.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">Keine Aktivitäten</div>
            ) : activitiesOnDashboard.map(activity => (
              <div key={activity.id} className="p-4">
                <div className="flex items-start gap-4">
                  <div className="rounded-full bg-primary/10 p-2 text-primary">
                    {activity.type === 'student' && <Users size={16} />}
                    {activity.type === 'teacher' && <UserCheck size={16} />}
                    {activity.type === 'course' && <BookOpen size={16} />}
                    {activity.type === 'trial' && <Calendar size={16} />}
                  </div>
                  <div>
                    <p className="text-sm">{activity.description}</p>
                    {/* Wer, bei wem, wann — der Satz allein sagte nicht, um wen es geht (T-0132). */}
                    <p className="mt-1 text-xs text-muted-foreground">
                      {[
                        activity.actorName,
                        activity.personName,
                        formatDateTime(activity.createdAt),
                      ].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    );
  };

  if (isLoading) {
    return <div className="mx-auto max-w-7xl space-y-6">
        <div>
          <Skeleton className="h-8 w-60" />
          <Skeleton className="mt-2 h-4 w-72" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, idx) => <Skeleton key={`kpi-skeleton-${idx + 1}`} className="h-32" />)}
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
        </div>
      </div>;
  }
  return <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Büro Dashboard</h1>
        <p className="text-muted-foreground">
          Willkommen bei der SIDIGO Musikschulverwaltung
        </p>
      </div>
      {/* Standort KPI Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {standortKpis.map((kpi, idx) => (
          <Card key={kpi.standortId} interactive className="p-4">
            <div className="flex items-center justify-between">
              <div className="font-semibold">{kpi.standortName}</div>
              <div className="flex items-center gap-1">
                {isEditMode ? (
                  <>
                    <Button variant="ghost" size="icon" disabled={idx === 0} onClick={() => moveKpi(idx, -1)} aria-label={`${kpi.standortName} nach oben verschieben`}><ArrowUp className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" disabled={idx === standortKpis.length - 1} onClick={() => moveKpi(idx, 1)} aria-label={`${kpi.standortName} nach unten verschieben`}><ArrowDown className="h-4 w-4" /></Button>
                  </>
                ) : null}
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-xs text-muted-foreground">Aufgaben</div>
                <div className="text-xl font-bold">{kpi.openOwnTasks}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Probestunden</div>
                <div className="text-xl font-bold">{kpi.upcomingTrials}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Anfragen</div>
                <div className="text-xl font-bold">{kpi.trialRequests}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>
      <Card className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Settings2 size={16} className="text-muted-foreground" />
            <span className="text-sm font-medium">Dashboard bearbeiten</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!isEditMode ? (
              <Button variant="outline" size="sm" onClick={startEditMode}>
                Dashboard editieren
              </Button>
            ) : (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      Bearbeitungsoptionen
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64">
                    <DropdownMenuLabel>Widgets</DropdownMenuLabel>
                    {erlaubteWidgetIds.map((widgetId) => (
                      <DropdownMenuCheckboxItem
                        key={widgetId}
                        checked={widgetVisibility[widgetId]}
                        onCheckedChange={(checked) => toggleWidgetVisibility(widgetId, checked === true)}
                      >
                        {WIDGET_LABELS[widgetId]}
                      </DropdownMenuCheckboxItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuCheckboxItem
                      checked={compactMode}
                      onCheckedChange={(checked) => toggleCompactMode(checked === true)}
                    >
                      Kompakt-Modus
                    </DropdownMenuCheckboxItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={showAllWidgets}>
                      <Eye className="mr-2 h-4 w-4" />
                      Alle Widgets anzeigen
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={resetDashboardLayout}>
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Layout zurücksetzen
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button variant="outline" size="sm" onClick={cancelEditMode}>
                  Abbrechen
                </Button>
                <Button size="sm" onClick={saveEditMode} disabled={!hasUnsavedChanges}>
                  Speichern
                </Button>
              </>
            )}
          </div>
        </div>
        {isEditMode ? (
          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {erlaubteWidgetIds.map((widgetId) => (
              <label key={widgetId} className="flex items-center justify-between rounded-md border px-3 py-2">
                <span className="text-sm">{WIDGET_LABELS[widgetId]}</span>
                <div className="flex items-center gap-2">
                  {widgetVisibility[widgetId] ? <Eye size={14} className="text-muted-foreground" /> : <EyeOff size={14} className="text-muted-foreground" />}
                  <Switch
                    checked={widgetVisibility[widgetId]}
                    onCheckedChange={(checked) => toggleWidgetVisibility(widgetId, checked)}
                    aria-label={`${WIDGET_LABELS[widgetId]} ein- oder ausblenden`}
                  />
                </div>
              </label>
            ))}
          </div>
        ) : null}
      </Card>
      <div ref={gridContainerRef}>
      {gridWidth > 0 ? (
      <Responsive
        width={gridWidth}
        className="layout"
        layouts={widgetLayouts}
        breakpoints={BREAKPOINTS}
        cols={COLS}
        rowHeight={42}
        margin={[16, 16]}
        containerPadding={[0, 0]}
        isDraggable={isDesktopEditMode}
        isResizable={isDesktopEditMode}
        draggableHandle=".widget-drag-handle"
        onBreakpointChange={(breakpoint) => setCurrentBreakpoint(breakpoint as DashboardBreakpoint)}
        onLayoutChange={handleWidgetLayoutsChange}
      >
        {visibleWidgetIds.map((widgetId) => (
          <div key={widgetId} className="min-h-0">
            <div className="h-full">
              {renderWidgetCard(widgetId)}
            </div>
          </div>
        ))}
      </Responsive>
      ) : null}
      </div>
      {visibleWidgetIds.length === 0 ? (
        <Card className="p-6 text-sm text-muted-foreground">
          Keine Widgets sichtbar. Aktiviere im Modus „Dashboard editieren“ mindestens ein Widget.
        </Card>
      ) : null}
      {hiddenWidgetIds.length > 0 ? (
        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">Ausgeblendete Widgets:</span>
            {hiddenWidgetIds.map((widgetId) => (
              <Button key={widgetId} variant="outline" size="sm" onClick={() => restoreWidget(widgetId)}>
                <Eye className="mr-2 h-4 w-4" />
                {WIDGET_LABELS[widgetId]} wiederherstellen
              </Button>
            ))}
          </div>
        </Card>
      ) : null}
      {trialForCancel && (
        <CancelTrialWizardModal
          open={cancelOpen}
          onOpenChange={(o) => { setCancelOpen(o); if (!o) setTrialForCancel(null); }}
          trial={trialForCancel}
          onDone={(updated) => {
            setTrials(prev => prev.filter(p => String(p.id) !== String(updated.id)));
          }}
        />
      )}
      {trialForEnroll && (
        <EnrollmentWizardModal
          open={enrollOpen}
          onOpenChange={(o) => { setEnrollOpen(o); if (!o) setTrialForEnroll(null); }}
          trialId={trialForEnroll.id}
          defaults={{
            weekday: (() => { try { const d = new Date(`${trialForEnroll.date}T00:00:00`); const jsDow = d.getDay(); return jsDow === 0 ? 7 : jsDow; } catch { return 1; } })(),
            startTime: trialForEnroll.startTime?.slice(0, 5) || '15:00',
            startDate: trialForEnroll.date,
          }}
          onCreated={async () => {
            if (!selectedStandortId) return;
            const today = new Date().toISOString().slice(0, 10);
            const trialItems = await fetchTrials(undefined, today, selectedStandortId);
            setTrials(trialItems.filter((t) => t.date <= today));
          }}
        />
      )}
      {trialForOffer && (
        <TrialOfferWizardModal
          open={offerOpen}
          onOpenChange={(o) => {
            setOfferOpen(o);
            if (!o) setTrialForOffer(null);
          }}
          trial={trialForOffer}
          standortId={selectedStandortId}
          onUpdated={async (updatedTrial) => {
            setTrials((prev) => prev.map((t) => (String(t.id) === String(updatedTrial.id) ? updatedTrial : t)));
            if (!selectedStandortId) return;
            const today = new Date().toISOString().slice(0, 10);
            const trialItems = await fetchTrials(undefined, today, selectedStandortId);
            setTrials(trialItems.filter((t) => t.date <= today));
          }}
        />
      )}
      <Dialog open={taskEditOpen} onOpenChange={setTaskEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Aufgabe bearbeiten</DialogTitle>
          </DialogHeader>
          {taskForEdit ? (
            <TaskForm
              initial={{
                title: taskForEdit.title || '',
                description: taskForEdit.description || '',
                dueAt: toInputDatetime(taskForEdit.dueAt),
                assignedUserId: taskForEdit.assignedUserId,
                standortId: selectedStandortId || undefined,
              }}
              onSubmit={handleTaskEditSubmit}
              onCancel={() => {
                setTaskEditOpen(false);
                setTaskForEdit(null);
              }}
              users={taskUsers}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>;
};
export default OfficeDashboard;